import React, { useState, useEffect, useMemo } from "react";
import {
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  MinusCircle,
  CircleAlert,
} from "lucide-react";

import {
  IAttendanceRegularization,
  RegularizationType,
} from "../../types/regularization.types";
import {
  getWorkflowStatusFallbackLabel,
  isApprovedWorkflowStatus,
} from "../../utils/constants";
import { SearchInput } from "../common";
import { useTimezone } from "../../hooks/useTimezone";
import { RegularizationTableSkeleton } from "../attendance/Skeleton";
import Badge from "../common/Badge";
import { getWorkflowStatusVariant } from "../../utils/badgeVariants";
import { TableColumn } from "../common/Table";
import { ColumnSelector } from "../common/Table/ColumnSelector";
import { DataTable, DataTableCard, Pagination2, EmployeeCell } from "../common/Table2";
import { getAvatarGradient } from "../../utils/avatarUtils";
import {
  useGetRegularizationReportQuery,
  useLazyGetRegularizationReportQuery,
  useGetRegularizationStatsQuery,
  IRegularizationReportFilters,
} from "../../store/apis/attendanceRegularization.api";
import {
  ReportTabPanelProps,
  useMountedGuard,
  useRegisterExport,
} from "./reportTabTypes";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";

const RegularizationReport: React.FC<ReportTabPanelProps> = ({
  onStatsChange,
  registerExport,
  period,
}) => {
  const { formatDate, formatTime } = useTimezone();

  const mountedRef = useMountedGuard();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Client-side search over the current page
  const [search, setSearch] = useState("");
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);

  // Get current month and year
  const currentDate = new Date();
  const currentMonth = currentDate.toISOString().slice(0, 7); // YYYY-MM
  const currentYear = currentDate.getFullYear().toString();
  const [filters, setFilters] = useState({
    month: currentMonth, // Pre-select current month
    year: currentYear, // Pre-select current year
  });

  useEffect(() => {
    if (!period?.year) return;
    handleFilterChange("year", String(period.year));
    if (period.month) {
      handleFilterChange("month", `${period.year}-${String(period.month).padStart(2, "0")}`);
    }
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period?.year, period?.month]);

  // Build report query params
  const reportQueryParams = useMemo<IRegularizationReportFilters>(() => ({
    month: filters.month,
    year: filters.year,
    page: currentPage,
    limit: itemsPerPage,
  }), [filters, currentPage, itemsPerPage]);

  const canViewRegularization = useHasPermission([
    PERMISSIONS.REPORTS_REGULARIZATION_VIEW,
    PERMISSIONS.REPORTS_REGULARIZATION_MANAGE,
  ]);

  // Fetch stats (once, no filters)
  const {
    data: statsData,
    isLoading: statsLoading
  } = useGetRegularizationStatsQuery(undefined, { skip: !canViewRegularization });

  // Fetch report (with filters and pagination)
  const {
    data: reportData,
    isLoading: reportLoading,
    error: reportError,
    isFetching: reportFetching
  } = useGetRegularizationReportQuery(reportQueryParams, { skip: !canViewRegularization });

  // Lazy query for Excel export
  const [triggerExport] = useLazyGetRegularizationReportQuery();

  // Map RTK Query IRegularizationRequest to IAttendanceRegularization
  const mapRequestToAttendanceRegularization = (req: any): IAttendanceRegularization => ({
    _id: req.id,
    userId: req.userId || '',
    fullName: req.fullName || '',
    employeeId: req.employeeId || '',
    departmentName: req.departmentName || '',
    date: req.date || '',
    regularizationType: req.regularizationTypeName || '',
    requestedCheckInTime: req.requestedCheckInTime || '',
    requestedCheckOutTime: req.requestedCheckOutTime || '',
    actualCheckInTime: req.actualCheckInTime,
    actualCheckOutTime: req.actualCheckOutTime,
    reason: req.reason || '',
    status: req.status,
    statusLabel: req.statusLabel,
    approvedByName: req.approvedByName,
    rejectionReason: req.rejectionReason,
    appliedDate: req.appliedDate || '',
    attendanceUpdated: req.attendanceUpdated || false,
    createdAt: req.createdAt,
    updatedAt: req.updatedAt,
  });

  // Derive state from RTK Query
  const stats = statsData || { total: 0, pending: 0, approved: 0, rejected: 0, approvalRate: '0.00%' };
  const requests = (reportData?.requests || []).map(mapRequestToAttendanceRegularization);
  const totalItems = reportData?.pagination?.total || 0;
  const loading = statsLoading || reportLoading;
  const tableLoading = reportFetching;
  const error = reportError ? (reportError as any)?.data?.message || 'Failed to load report' : '';

  // Feed the shared StatCards row in the Reports shell
  useEffect(() => {
    onStatsChange?.({
      totalRequests: stats.total ?? 0,
      pending: stats.pending ?? 0,
      approved: stats.approved ?? 0,
      rejected: stats.rejected ?? 0,
    });
  }, [stats, onStatsChange]);

  // Client-side search over the current page (the report API has no search param)
  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter(
      (r) =>
        r.fullName?.toLowerCase().includes(q) ||
        r.employeeId?.toLowerCase().includes(q),
    );
  }, [requests, search]);

  const getRegularizationTypeLabel = (type: string | RegularizationType) => {
    if (!type) return "—";
    const labels: Record<string, string> = {
      [RegularizationType.FORGOT_CHECKIN]: "Forgot Check-in",
      [RegularizationType.LATE_CHECKIN]: "Late Check-in",
      [RegularizationType.FORGOT_CHECKOUT]: "Forgot Check-out",
      [RegularizationType.EARLY_CHECKOUT]: "Early Check-out",
      [RegularizationType.MISSED_BREAK]: "Missed Break",
      [RegularizationType.SYSTEM_ERROR]: "System Error",
      [RegularizationType.OTHER]: "Other",
    };
    if (labels[type]) return labels[type];
    return String(type)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getWorkflowStatusIcon = (status?: string) => {
    if (!status) return MinusCircle;

    if (status.startsWith("pending")) {
      return Clock;
    }

    if (status.startsWith("approved")) {
      return CheckCircle;
    }

    if (status.startsWith("rejected")) {
      return XCircle;
    }

    if (status === "cancelled") {
      return AlertCircle;
    }

    return CircleAlert;
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1);
  };

  const toggleVisibility = (key: string) => {
    setHiddenKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const exportToCSV = async () => {
    try {
      const { page, limit, ...downloadParams } = reportQueryParams;
      const result = await triggerExport(downloadParams).unwrap();

      if (!mountedRef.current) return;
      const allRequests = (result?.requests || []).map(mapRequestToAttendanceRegularization);

      const headers = [
        "Employee Name",
        "Employee ID",
        "Department",
        "Date",
        "Type",
        "Requested Check-in",
        "Requested Check-out",
        "Reason",
        "Status",
        "Applied Date",
        "Approved By",
        "Rejection Reason",
      ];

      const csvData = allRequests.map((request) => [
        request.fullName,
        request.employeeId,
        request.departmentName,
        request.date,
        getRegularizationTypeLabel(request.regularizationType),
        request.requestedCheckInTime,
        request.requestedCheckOutTime,
        request.reason,
        request.status,
        request.appliedDate,
        (request as any).approvedByName || "",
        request.rejectionReason || "",
      ]);

      const csvContent = [headers, ...csvData]
        .map((row) => row.map((field) => `"${field}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `regularization-report-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting CSV:", err);
    }
  };

  useRegisterExport(exportToCSV, registerExport);

  const columns = useMemo<TableColumn<IAttendanceRegularization>[]>(() => [
    {
      key: "employee",
      title: "Employee",
      label: "Employee",
      required: true,
      dataIndex: "fullName",
      width: "30%",
      render: (_, record) => (
        <EmployeeCell
          name={record.fullName || ""}
          sub={`${record.employeeId} • ${record.departmentName}`}
          avatarBg={getAvatarGradient(record.fullName || "")}
        />
      )
    },
    {
      key: "dateType",
      title: "Date",
      dataIndex: "date",
      width: "20%",
      render: (_, record) => (
        <div>
          <div className="text-label text-[var(--color-text-primary)]">
            {formatDate(record.date)}
          </div>
          <div className="mt-1">
            <span className="inline-flex items-center px-[8px] py-[3px] rounded-[6px] bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)] text-caption">
              {getRegularizationTypeLabel(record.regularizationType)}
            </span>
          </div>
        </div>
      )
    },
    {
      key: "requestedTime",
      title: "Requested",
      width: "20%",
      render: (_, record) => (
        <div className="text-[13px] font-[500] text-[var(--color-text-secondary)]">
          {formatTime(record.requestedCheckInTime)} - {formatTime(record.requestedCheckOutTime)}
        </div>
      )
    },
    {
      key: "status",
      title: "Status",
      label: "Status",
      required: true,
      width: "15%",
      render: (_, record) => {
        const StatusIcon = getWorkflowStatusIcon(record.status);
        const approverName = (record as any).approvedByName as string | undefined;
        const shouldShowApprover = isApprovedWorkflowStatus(record.status) && !!approverName;
        const variant = getWorkflowStatusVariant(record.status);

        return (
          <div>
            <Badge variant={variant} size="middle" icon={<StatusIcon size={12} className="shrink-0" />}>
              {getWorkflowStatusFallbackLabel(record.status)}
            </Badge>
            {shouldShowApprover && (
              <div className="text-body-sm text-[var(--color-text-tertiary)] mt-1">
                by {approverName}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: "appliedDate",
      title: "Applied Date",
      dataIndex: "appliedDate",
      width: "15%",
      render: (_, record) => (
        <span className="text-sm text-[var(--color-text-secondary)]">
          {formatDate(record.appliedDate)}
        </span>
      )
    }
  ], [formatDate, formatTime]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.required || !hiddenKeys.includes(c.key)),
    [columns, hiddenKeys],
  );

  const optionalVisibleKeys = useMemo(
    () =>
      columns
        .filter((c) => !c.required && !hiddenKeys.includes(c.key))
        .map((c) => c.key),
    [columns, hiddenKeys],
  );

  return (
    <div className="space-y-5">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <DataTableCard
        title="Regularization Report"
        count={totalItems}
        toolbar={
          <>
            <SearchInput
              placeholder="Search employee..."
              value={search}
              onChange={setSearch}
              className="w-full sm:w-[220px]"
            />
            <ColumnSelector
              columns={columns}
              visibleKeys={optionalVisibleKeys}
              onToggle={toggleVisibility}
            />
          </>
        }
        pagination={
          <Pagination2
            total={totalItems}
            page={currentPage}
            pageSize={itemsPerPage}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            onPageChange={handlePageChange}
            onPageSizeChange={handleItemsPerPageChange}
          />
        }
      >
        <DataTable
          columns={visibleColumns}
          rows={filteredRequests}
          rowKey={(r) => r._id || `${r.employeeId}-${r.date}`}
          loading={loading || tableLoading}
          skeleton={<RegularizationTableSkeleton />}
          emptyMessage="No regularization requests match the current filters"
          responsive="scroll"
        />
      </DataTableCard>
    </div>
  );
};

export default RegularizationReport;
